/**
 * RBAC model for Hostesa frontend.
 *
 * Permission union exactly matches §11 of the frontend master prompt and the
 * permission matrix in hostesa-backend/docs/frontend/02-rbac-and-conventions.md.
 *
 * IMPORTANT: UI checks are UX-only. The backend is the authoritative security
 * boundary — always handle 403 responses gracefully regardless of what the
 * client-side check says.
 */

import type { Role } from '@hostesa/api-client';

export type Permission =
  | 'tenant:read'
  | 'hotel:read'
  | 'hotel:configure'
  | 'user:read'
  | 'user:manage'
  | 'call:read'
  | 'conversation:read'
  | 'reservation:read'
  | 'reservation:write'
  | 'booking:read'
  | 'booking:create'
  | 'booking:confirm'
  | 'booking:cancel'
  | 'knowledge:read'
  | 'knowledge:write'
  | 'knowledge:approve'
  | 'handoff:handle'
  | 'service_request:read'
  | 'service_request:manage'
  | 'onboarding:read'
  | 'onboarding:manage'
  | 'integration:read'
  | 'integration:manage'
  | 'usage:read'
  | 'audit:read'
  | 'admin:cross_tenant_read'
  | 'admin:impersonate'
  | 'admin:provision'
  | 'admin:hotel_configure';

/**
 * Maps each dashboard role to its allowed permissions.
 *
 * Derived from the matrix in hostesa-backend/docs/frontend/02-rbac-and-conventions.md.
 * Keep in sync when the backend permission table changes — the server is authoritative.
 *
 * `ai_actor` is intentionally excluded: it is not a dashboard user and never
 * authenticates via a session login.
 */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  hotel_admin: [
    // Tenant & hotel
    'tenant:read',
    'hotel:read',
    'hotel:configure',
    // Users
    'user:read',
    'user:manage',
    // Calls & conversations
    'call:read',
    'conversation:read',
    // Reservations & bookings
    'reservation:read',
    'reservation:write',
    'booking:read',
    'booking:create',
    'booking:confirm',
    'booking:cancel',
    // Knowledge
    'knowledge:read',
    'knowledge:write',
    'knowledge:approve',
    // Handoff
    'handoff:handle',
    // Service requests
    'service_request:read',
    'service_request:manage',
    // Onboarding
    'onboarding:read',
    'onboarding:manage',
    // Integrations
    'integration:read',
    'integration:manage',
    // Usage & audit
    'usage:read',
    'audit:read',
  ],
  hotel_staff: [
    // Hotel (no tenant-level access)
    'hotel:read',
    // Calls & conversations
    'call:read',
    'conversation:read',
    // Reservations & bookings
    'reservation:read',
    'reservation:write',
    'booking:read',
    'booking:create',
    'booking:confirm',
    // Knowledge (read-only)
    'knowledge:read',
    // Handoff
    'handoff:handle',
    // Service requests
    'service_request:read',
    'service_request:manage',
    // Onboarding (read-only)
    'onboarding:read',
  ],
  hostesa_admin: [
    // Cross-tenant read-only visibility
    'tenant:read',
    'hotel:read',
    // Users (read-only oversight)
    'user:read',
    // Calls & conversations
    'call:read',
    'conversation:read',
    // Reservations & bookings (read-only)
    'reservation:read',
    'booking:read',
    // Knowledge (read-only)
    'knowledge:read',
    // Service requests (read-only)
    'service_request:read',
    // Onboarding (read-only)
    'onboarding:read',
    // Integrations (read-only)
    'integration:read',
    // Usage & audit
    'usage:read',
    'audit:read',
    // Backoffice admin permissions
    'admin:cross_tenant_read',
    'admin:impersonate',
    'admin:provision',
    'admin:hotel_configure',
  ],
  // Support: impersonate + cross-tenant read, but NOT provision.
  hostesa_support: [
    'tenant:read',
    'hotel:read',
    'user:read',
    'call:read',
    'conversation:read',
    'reservation:read',
    'booking:read',
    'knowledge:read',
    'service_request:read',
    'onboarding:read',
    'integration:read',
    'usage:read',
    'audit:read',
    'admin:cross_tenant_read',
    'admin:impersonate',
  ],
  // Read-only / on-call: cross-tenant visibility only — no impersonate, no provision.
  hostesa_readonly: [
    'tenant:read',
    'hotel:read',
    'user:read',
    'call:read',
    'conversation:read',
    'reservation:read',
    'booking:read',
    'knowledge:read',
    'service_request:read',
    'onboarding:read',
    'integration:read',
    'usage:read',
    'audit:read',
    'admin:cross_tenant_read',
  ],
};

/**
 * Returns true when {@link role} has been granted {@link permission}.
 *
 * This is the RBAC half of `allowed = hasPermission(role, perm) && hasScope(membership, scope)`.
 * Do not use this alone for scoped resources — combine with {@link hasScope} from scope.ts.
 */
export function hasPermission(role: Role, permission: Permission): boolean {
  return (ROLE_PERMISSIONS[role] as readonly Permission[]).includes(permission);
}
